import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiDownload } from 'react-icons/fi';

import { Button } from '@shared/ui/button';
import DataTable from '@shared/ui/data-table';
import type { Column } from '@shared/ui/data-table';
import PaginationButtons from '@shared/ui/pagination-buttons';
import { PushBadge } from '@entities/marketing-push/ui';
import {
  buildErrorsCsv,
  describeErrorCode,
  downloadCsv,
  formatPushNumber,
  reportFilename,
} from '@entities/marketing-push/lib';
import {
  asCount,
  asList,
  marketingPushApi,
  pushError,
} from '../../api/services/marketingPush.service';
import {
  CAMPAIGN_STATUS_LABEL,
  DELIVERY_STATUS_LABEL,
} from '../../types/marketingPushInterfaces';
import type {
  PushDeliveryErrorRow,
  PushLanguage,
  PushReport,
} from '../../types/marketingPushInterfaces';

const CARD = 'bg-white rounded-xl shadow-sm p-4 mb-4';
const PAGE_SIZE = 10;
const LIVE_STATUSES = ['queued', 'running', 'handed_off'];

/**
 * Отчёт по отправке.
 *
 * Это то место, где новый сервис принципиально отличается от старого.
 * Старый хранил только агрегаты — сколько успешно, сколько нет; назвать
 * причину по конкретному получателю было невозможно, потому что строк на
 * адресатов не существовало. Здесь они есть, поэтому отчёт показывает
 * и сводку, и разбор ошибок с расшифровкой кодов FCM.
 */
export default function PushCampaignReport() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaignId = Number(params.id);

  const [report, setReport] = useState<PushReport | undefined>();
  const [languages, setLanguages] = useState<PushLanguage[]>([]);
  const [errorRows, setErrorRows] = useState<PushDeliveryErrorRow[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    marketingPushApi
      .getLanguages()
      .then((response) => setLanguages(asList(response.data)))
      .catch(() => setLanguages([]));
  }, []);

  const loadReport = useCallback(async () => {
    try {
      const { data } = await marketingPushApi.getReport(campaignId);
      setReport(data);
    } catch (loadError) {
      setError(pushError(loadError, 'Не удалось загрузить отчёт'));
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const loadErrors = useCallback(async () => {
    try {
      const { data } = await marketingPushApi.getReportErrors(campaignId, {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setErrorRows(asList(data));
      setErrorCount(asCount(data));
    } catch {
      setErrorRows([]);
    }
  }, [campaignId, page]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  /* Пока рассылка идёт, числа меняются — обновляем сами, чтобы маркетолог
     не жал F5. Как только статус стал терминальным, опрос прекращается. */
  useEffect(() => {
    if (!report || !LIVE_STATUSES.includes(report.status)) return;
    const timer = window.setInterval(() => {
      loadReport();
      loadErrors();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [loadErrors, loadReport, report]);

  /**
   * Выгрузка собирает все страницы, а не только открытую: маркетологу нужен
   * полный список причин, а таблица на экране постраничная. Ограничение
   * в 200 страниц — предохранитель от бесконечного цикла, если сервер
   * начнёт отдавать count и results несогласованно.
   */
  async function exportErrors() {
    setExporting(true);
    try {
      const collected: PushDeliveryErrorRow[] = [];
      for (let index = 0; index < 200; index += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await marketingPushApi.getReportErrors(campaignId, {
          limit: 100,
          offset: index * 100,
        });
        const chunk = asList(data);
        collected.push(...chunk);
        if (chunk.length < 100) break;
      }
      downloadCsv(reportFilename(campaignId), buildErrorsCsv(collected));
    } catch (exportError) {
      setError(pushError(exportError, 'Не удалось выгрузить отчёт'));
    } finally {
      setExporting(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Загрузка…</div>;
  if (!report) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error || 'Отчёт недоступен.'}
        </div>
      </div>
    );
  }

  const { totals } = report;
  const accepted = totals.sent + totals.delivered;

  const errorColumns: Column<PushDeliveryErrorRow>[] = [
    {
      key: 'installation',
      header: 'Установка',
      render: (row) => <span className="font-mono text-xs">#{row.installation_id}</span>,
    },
    {
      key: 'target',
      header: 'Приложение',
      render: (row) => <span className="text-xs text-[#2d2c2c]">{row.app_target__code}</span>,
    },
    {
      key: 'language',
      header: 'Язык',
      render: (row) => (
        <span className="text-xs text-[#2d2c2c]">
          {languages.find((language) => language.code === row.language__code)?.name ??
            row.language__code}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (row) => (
        <span className="text-xs text-[#2d2c2c]">
          {DELIVERY_STATUS_LABEL[row.status] ?? row.status}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Причина',
      render: (row) => {
        const meta = describeErrorCode(row.error_code);
        return (
          <div>
            <PushBadge tone={meta.tone}>{meta.label}</PushBadge>
            <p className="mt-1 text-xs text-gray-500">{meta.hint}</p>
          </div>
        );
      },
    },
    {
      key: 'attempts',
      header: 'Попыток',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => <span className="text-xs text-[#2d2c2c]">{row.attempts}</span>,
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/marketing/push/${campaignId}`)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <FiArrowLeft /> К рассылке
        </button>
        <h1 className="text-xl font-semibold text-[#2d2c2c]">
          Отчёт по рассылке #{campaignId}
        </h1>
        <PushBadge tone={CAMPAIGN_STATUS_LABEL[report.status].tone}>
          {CAMPAIGN_STATUS_LABEL[report.status].label}
        </PushBadge>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className={CARD}>
        <h2 className="mb-3 text-base font-semibold text-[#2d2c2c]">Сводка</h2>

        <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
          <Metric label="Аудитория" value={formatPushNumber(report.locked_audience_size)} />
          <Metric label="Принято FCM" value={formatPushNumber(accepted)} />
          <Metric
            label="Подтверждено"
            value={formatPushNumber(totals.delivered)}
            hint="Приложение сообщило о получении"
          />
          <Metric label="Прочитано" value={formatPushNumber(totals.read)} />
          <Metric label="Переходов" value={formatPushNumber(totals.clicked)} />
          <Metric
            label="Не доставлено"
            value={formatPushNumber(totals.failed + totals.skipped)}
            danger={totals.failed + totals.skipped > 0}
          />
        </div>

        {totals.queued > 0 && (
          <div className="mt-4 rounded-lg bg-sky-50 p-3 text-sm text-sky-800">
            <strong className="block">Отправка ещё идёт</strong>
            В очереди осталось {formatPushNumber(totals.queued)}. Числа обновляются
            автоматически.
          </div>
        )}

        <p className="mt-4 text-xs text-gray-500">
          «Принято FCM» означает, что сервис Google взял сообщение в доставку.
          Подтверждение получения на устройстве приходит от самого приложения, поэтому
          эти два числа не совпадают: часть устройств выключена или без сети.
        </p>
      </div>

      <div className={CARD}>
        <h2 className="mb-3 text-base font-semibold text-[#2d2c2c]">По приложениям</h2>
        <DataTable
          columns={[
            {
              key: 'target',
              header: 'Приложение',
              render: (row: [string, { total: number; accepted: number }]) => (
                <span className="text-[#2d2c2c]">{row[0]}</span>
              ),
            },
            {
              key: 'total',
              header: 'Аудитория',
              headerClassName: 'text-right',
              className: 'text-right',
              render: (row: [string, { total: number; accepted: number }]) =>
                formatPushNumber(row[1].total),
            },
            {
              key: 'accepted',
              header: 'Принято',
              headerClassName: 'text-right',
              className: 'text-right',
              render: (row: [string, { total: number; accepted: number }]) =>
                formatPushNumber(row[1].accepted),
            },
          ]}
          data={Object.entries(report.by_target)}
          keyAccessor={(row) => row[0]}
          emptyMessage="Нет данных"
        />
      </div>

      {Object.keys(report.by_error).length > 0 && (
        <div className={CARD}>
          <h2 className="mb-3 text-base font-semibold text-[#2d2c2c]">Причины ошибок</h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(report.by_error).map(([code, count]) => {
              const meta = describeErrorCode(code);
              return (
                <div
                  key={code}
                  className="min-w-[260px] flex-1 rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <PushBadge tone={meta.tone}>{meta.label}</PushBadge>
                    <strong className="text-[#2d2c2c]">{formatPushNumber(count)}</strong>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">{meta.hint}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={CARD}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-[#2d2c2c]">
            Необработанные получатели{' '}
            <span className="text-sm font-normal text-gray-500">
              {formatPushNumber(errorCount)}
            </span>
          </h2>
          <Button
            isWhite
            disabled={exporting || errorCount === 0}
            onClick={() => void exportErrors()}
          >
            <span className="inline-flex items-center gap-1">
              <FiDownload /> {exporting ? 'Готовим…' : 'Выгрузить CSV'}
            </span>
          </Button>
        </div>

        <DataTable
          columns={errorColumns}
          data={errorRows}
          keyAccessor={(row) => row.installation_id}
          emptyMessage="Все получатели обработаны"
        />

        {errorCount > PAGE_SIZE && (
          <div className="mt-4">
            <PaginationButtons
              currentPage={page}
              setCurrentPage={setPage}
              ordersLength={errorCount}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
  danger,
}: {
  label: string;
  value: string;
  hint?: string;
  danger?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div
        className={`text-2xl font-semibold leading-tight ${
          danger ? 'text-red-600' : 'text-[#2d2c2c]'
        }`}
      >
        {value}
      </div>
      {hint && <div className="text-xs text-gray-500">{hint}</div>}
    </div>
  );
}
